/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file WorkLocationDisclosureSheet.js
 * @description
 * Prominent disclosure shown ONCE before the OS location prompt for the work-event location pings (clock in/out,
 * work-order start, app open). Sibling of the driver route-tracking `LocationDisclosureSheet`, with its own copy and
 * its own consent state (`workLocation.DISCLOSURE_KEY`).
 *
 * @module components/workLocation/WorkLocationDisclosureSheet
 */

import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {DriverSheet} from '../driver/DriverUI';

const ORANGE = '#F97316';

const Row = ({icon, title, text}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.row}>
            <View style={styles.icon}><Icon name={icon} size={20} color={ORANGE} /></View>
            <View style={styles.text}>
                <Text style={[styles.title, {color: m.colors.heading}]}>{title}</Text>
                <Text style={[styles.body, {color: m.colors.subText}]}>{text}</Text>
            </View>
        </View>
    );
};

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {Function} props.onAllow Continue to the OS permission prompt.
 * @param {Function} props.onNotNow Dismiss; no location is sent and the user is not asked again for 7 days.
 * @returns {JSX.Element} The sheet.
 */
const WorkLocationDisclosureSheet = ({visible, onAllow, onNotNow}) => {
    const {t} = useTranslation();

    return (
        <DriverSheet
            visible={visible}
            icon="map-marker-account-outline"
            title={t('worklocation_disclosure_title', {defaultValue: 'Munkahelyi helyadat'})}
            subtitle={t('worklocation_disclosure_sub', {defaultValue: 'Kérjük, olvasd el, mielőtt engedélyezed.'})}
            onClose={onNotNow}
            confirmLabel={t('worklocation_allow', {defaultValue: 'Engedélyezem'})}
            confirmIcon="check"
            cancelLabel={t('worklocation_not_now', {defaultValue: 'Most nem'})}
            onConfirm={onAllow}
        >
            <Row
                icon="map-marker-radius-outline"
                title={t('worklocation_what_title', {defaultValue: 'Mit rögzítünk'})}
                text={t('worklocation_what_text', {defaultValue: 'A telefonod pozícióját (koordináta és pontosság) kizárólag munkaesemények pillanatában: munkakezdéskor és munkavégzés végén, munkalap indításakor és az alkalmazás megnyitásakor.'})}
            />
            <Row
                icon="timer-sand"
                title={t('worklocation_when_title', {defaultValue: 'Mikor nem'})}
                text={t('worklocation_when_text', {defaultValue: 'Folyamatos követés nincs: az alkalmazás a háttérben és zárolt képernyő mellett nem rögzít helyadatot.'})}
            />
            <Row
                icon="account-eye-outline"
                title={t('worklocation_who_title', {defaultValue: 'Ki látja'})}
                text={t('worklocation_who_text', {defaultValue: 'A munkáltatód rögzíti, és az üzlet vezetői láthatják a munkahelyi térképen.'})}
            />
            <Row
                icon="shield-check-outline"
                title={t('worklocation_choice_title', {defaultValue: 'A te döntésed'})}
                text={t('worklocation_choice_text', {defaultValue: 'Engedély nélkül is használhatod az alkalmazást és rögzítheted a munkaidődet, csak helyadat nem kerül elküldésre. Az engedélyt a telefon beállításaiban bármikor visszavonhatod.'})}
            />
        </DriverSheet>
    );
};

const styles = StyleSheet.create({
    row: {flexDirection: 'row', gap: 12, marginBottom: 14},
    icon: {width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(249,115,22,0.14)'},
    text: {flex: 1},
    title: {fontSize: 15, fontWeight: '800'},
    body: {fontSize: 13, fontWeight: '600', lineHeight: 19, marginTop: 2},
});

export default WorkLocationDisclosureSheet;
